import { useState, useCallback, useEffect } from 'react';
import { getTranslations } from './i18n';

// API base URL — same gateway used by the rest of public-web
const API_BASE = 'https://gyyakwn6qc.execute-api.eu-south-2.amazonaws.com/dev';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Validate email format */
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Format an ISO date string to a locale-friendly date string */
function formatDate(isoString, locale) {
  try {
    return new Date(isoString).toLocaleDateString(locale === 'en' ? 'en-GB' : 'es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoString;
  }
}

/** Format an ISO date string to a locale-friendly time string */
function formatTime(isoString, timezone, locale) {
  try {
    return new Date(isoString).toLocaleTimeString(locale === 'en' ? 'en-GB' : 'es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone || undefined,
    });
  } catch {
    return isoString;
  }
}

/** Return YYYY-MM-DD for a Date object */
function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Simple spinner */
function Spinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-10" role="status" aria-label={label}>
      <svg
        className="w-8 h-8 animate-spin text-blue-600"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {label && <p className="mt-3 text-sm text-gray-500">{label}</p>}
    </div>
  );
}

/**
 * Monthly calendar grid.
 * Disables past dates and dates more than MAX_DAYS_OUT days from today.
 */
const MAX_DAYS_OUT = 90;

function MonthCalendar({ t, onSelectDate, loadingDate, configData, availableDates, onMonthChange, initialMonth }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + (configData?.bookingWindowDays || MAX_DAYS_OUT));

  const [viewYear, setViewYear] = useState(initialMonth?.year || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialMonth?.month ?? today.getMonth()); // 0-indexed

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => { onMonthChange?.(y - 1, 11); return y - 1; });
    } else {
      setViewMonth((m) => { onMonthChange?.(viewYear, m - 1); return m - 1; });
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => { onMonthChange?.(y + 1, 0); return y + 1; });
    } else {
      setViewMonth((m) => { onMonthChange?.(viewYear, m + 1); return m + 1; });
    }
  };

  // Build the grid: weeks × 7 days
  // First day of the month
  const firstDay = new Date(viewYear, viewMonth, 1);
  // Day of week for first day (0=Sun … 6=Sat); convert to Mon-first (0=Mon … 6=Sun)
  const startDow = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells = [];
  // Leading empty cells
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Trailing empty cells to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const canGoPrev = !(viewYear === today.getFullYear() && viewMonth === today.getMonth());
  const canGoNext = new Date(viewYear, viewMonth + 1, 1) <= maxDate;

  return (
    <div className="w-full">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          aria-label={t.previousMonth}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          ‹
        </button>
        <span className="font-semibold text-gray-800">
          {t.months[viewMonth]} {viewYear}
        </span>
        <button
          onClick={goToNextMonth}
          disabled={!canGoNext}
          aria-label={t.nextMonth}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          ›
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {t.days.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, idx) => {
          if (!day) {
            return <div key={`empty-${idx}`} />;
          }

          const cellDate = new Date(viewYear, viewMonth, day);
          cellDate.setHours(0, 0, 0, 0);
          const isTooFar = cellDate > maxDate;
          const ymd = toYMD(cellDate);
          const isAvailable = availableDates.has(ymd);
          const isDisabled = isTooFar || !isAvailable;
          const isLoading = loadingDate === ymd;
          const isToday = cellDate.getTime() === today.getTime();

          return (
            <button
              key={day}
              onClick={() => !isDisabled && onSelectDate(ymd)}
              disabled={isDisabled}
              aria-label={`${day} ${t.months[viewMonth]} ${viewYear}`}
              className={[
                'relative mx-auto flex items-center justify-center w-9 h-9 text-sm transition-colors',
                isDisabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'bg-slate-100 text-slate-900 font-medium hover:bg-primary-700 hover:text-white cursor-pointer',
                isToday && !isDisabled ? 'ring-1 ring-primary-700' : '',
                isLoading ? 'bg-primary-700 text-white' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isLoading ? (
                <svg className="w-4 h-4 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                day
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * BookingComponent — embeddable Calendly-style booking widget.
 *
 * Props:
 *   bookingToken  {string}   required — identifies the booking config
 *   locale        {string}   optional — 'es' (default) or 'en'
 *   onBookingConfirmed {function} optional — called with booking object on success
 */
function BookingComponent({ bookingToken, locale = 'es', source = 'demo', onBookingConfirmed, prefillName = '', prefillEmail = '', prefillSubject = '' }) {
  const t = getTranslations(locale);

  // State machine
  // States: 'loading-config' | 'date-selection' | 'loading' | 'slot-selection' | 'form' | 'submitting' | 'confirmation' | 'error'
  const [state, setState] = useState('loading-config');

  // Config (preloaded)
  const [configData, setConfigData] = useState(null);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [initialMonth, setInitialMonth] = useState(null);

  // Data
  const [loadingDate, setLoadingDate] = useState(null); // date being fetched (shows spinner on cell)
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD
  const [slots, setSlots] = useState([]);
  const [timezone, setTimezone] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [slotConflictMessage, setSlotConflictMessage] = useState('');

  // Form fields
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', comments: '', privacy: false, honeypot: '' });
  const [formErrors, setFormErrors] = useState({});
  const [formLoadedAt] = useState(Date.now());

  // ── Preload config on mount ───────────────────────────────────────────────
  const fetchMonthAvailability = useCallback(async (year, month) => {
    try {
      const mm = String(month + 1).padStart(2, '0');
      const res = await fetch(
        `${API_BASE}/bookingGetAvailability?token=${encodeURIComponent(bookingToken)}&month=${year}-${mm}`
      );
      if (res.ok) {
        const data = await res.json();
        const dates = new Set(data.availableDates || []);
        setAvailableDates(dates);
        return dates;
      }
    } catch { /* ignore */ }
    return new Set();
  }, [bookingToken]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/bookingGetAvailability?token=${encodeURIComponent(bookingToken)}`);
        if (res.ok) {
          const data = await res.json();
          setConfigData(data);
        }
      } catch { /* ignore */ }
      // Fetch current month availability; if empty, try next month
      const now = new Date();
      const dates = await fetchMonthAvailability(now.getFullYear(), now.getMonth());
      if (dates.size === 0) {
        const nextMonth = now.getMonth() === 11 ? 0 : now.getMonth() + 1;
        const nextYear = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
        await fetchMonthAvailability(nextYear, nextMonth);
        setInitialMonth({ year: nextYear, month: nextMonth });
      }
      setState('date-selection');
    })();
  }, [fetchMonthAvailability]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelectDate = useCallback(
    async (ymd) => {
      setLoadingDate(ymd);
      setSlotConflictMessage('');
      try {
        const res = await fetch(
          `${API_BASE}/bookingGetAvailability?token=${encodeURIComponent(bookingToken)}&date=${ymd}`
        );
        const data = await res.json();

        if (!res.ok) {
          setErrorMessage(data.error || t.errorGeneric);
          setState('error');
          return;
        }

        setSelectedDate(ymd);
        setSlots(data.slots || []);
        setTimezone(data.timezone || null);
        setState('slot-selection');
      } catch {
        setErrorMessage(t.errorGeneric);
        setState('error');
      } finally {
        setLoadingDate(null);
      }
    },
    [bookingToken, t]
  );

  const handleSelectSlot = useCallback((slot) => {
    setSelectedSlot(slot);
    setFormData({ name: prefillName || '', email: prefillEmail || '', phone: '', subject: '', comments: '', privacy: false, honeypot: '' });
    setFormErrors({});

    // If email is prefilled, skip the form and submit directly
    if (prefillEmail && isValidEmail(prefillEmail)) {
      setState('submitting');
      submitBooking(slot, { name: prefillName || '', email: prefillEmail, phone: '', subject: prefillSubject || '', comments: '' });
    } else {
      setState('form');
    }
  }, [prefillName, prefillEmail, prefillSubject]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitBooking = async (slot, data) => {
    try {
      const res = await fetch(`${API_BASE}/bookingCreateBooking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingToken,
          slotStartTime: slot.startTime,
          name: (data.name || '').trim(),
          email: (data.email || '').trim(),
          phone: (data.phone || '').trim(),
          subject: (data.subject || '').trim(),
          comments: (data.comments || '').trim(),
          source,
          timestamp: formLoadedAt,
          timeSinceLoad: Date.now() - formLoadedAt,
        }),
      });

      const result = await res.json();

      if (res.status === 409) {
        setSlotConflictMessage(t.errorSlotTaken);
        setState('slot-selection');
        return;
      }

      if (res.status === 429) {
        setErrorMessage(t.errorRateLimit);
        setState('error');
        return;
      }

      if (!res.ok) {
        throw new Error(result.error || 'Error');
      }

      setBooking({ ...result, name: data.name.trim(), email: data.email.trim() });
      if (onBookingConfirmed) onBookingConfirmed(result);
      setState('confirmation');
    } catch (err) {
      console.error('Booking error:', err);
      setErrorMessage(t.errorGeneric);
      setState('error');
    }
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.phone.trim()) errors.phone = t.phoneRequired;
    if (formData.email && !isValidEmail(formData.email)) errors.email = t.emailInvalid;
    if (!formData.privacy) errors.privacy = t.privacyRequired;
    return errors;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Anti-spam: honeypot
    if (formData.honeypot) return;

    // Anti-spam: minimum time on page (3 seconds)
    if (Date.now() - formLoadedAt < 3000) {
      console.warn('[BookingComponent] Anti-spam: too fast, ignoring submit');
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setState('submitting');
    try {
      const res = await fetch(`${API_BASE}/bookingCreateBooking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingToken,
          slotStartTime: selectedSlot.startTime,
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          subject: (formData.subject || prefillSubject || '').trim(),
          comments: (formData.comments || '').trim(),
          source,
          timestamp: formLoadedAt,
          timeSinceLoad: Date.now() - formLoadedAt,
        }),
      });

      const data = await res.json();

      if (res.status === 409) {
        // Slot taken — go back to slot selection with message
        setSlotConflictMessage(t.errorSlotTaken);
        setState('slot-selection');
        return;
      }

      if (res.status === 429) {
        setErrorMessage(t.errorRateLimit);
        setState('error');
        return;
      }

      if (!res.ok) {
        setErrorMessage(data.error || t.errorGeneric);
        setState('error');
        return;
      }

      setBooking(data);
      setState('confirmation');
      if (typeof onBookingConfirmed === 'function') {
        onBookingConfirmed(data);
      }
    } catch {
      setErrorMessage(t.errorGeneric);
      setState('error');
    }
  };

  const handleReset = () => {
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot(null);
    setBooking(null);
    setErrorMessage('');
    setSlotConflictMessage('');
    setFormData({ name: '', email: '', phone: '', subject: '', comments: '', privacy: false, honeypot: '' });
    setFormErrors({});
    setState('date-selection');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="w-full max-w-md mx-auto bg-white border border-slate-300 overflow-hidden"
      style={{ minWidth: 0 }}
    >
      <div className="p-4 sm:p-6">
        {/* LOADING CONFIG */}
        {state === 'loading-config' && <Spinner label={t.loadingAvailability} />}

        {/* DATE SELECTION */}
        {state === 'date-selection' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t.selectDate}</h2>
            <MonthCalendar
              t={t}
              onSelectDate={handleSelectDate}
              loadingDate={loadingDate}
              configData={configData}
              availableDates={availableDates}
              onMonthChange={fetchMonthAvailability}
              initialMonth={initialMonth}
            />
          </div>
        )}

        {/* LOADING (full-screen spinner, used for submitting) */}
        {state === 'loading' && <Spinner label={t.loadingAvailability} />}

        {/* SLOT SELECTION */}
        {state === 'slot-selection' && (
          <div>
            <button
              onClick={() => setState('date-selection')}
              className="text-sm text-blue-600 hover:underline mb-4 block"
            >
              {t.backToCalendar}
            </button>
            <h2 className="text-lg font-semibold text-gray-800 mb-1">{t.selectSlot}</h2>
            <p className="text-sm text-gray-500 mb-4">
              {t.slotFor} {selectedDate}
            </p>

            {slotConflictMessage && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                {slotConflictMessage}
              </div>
            )}

            {slots.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">{t.noSlotsAvailable}</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {slots.map((slot) => (
                  <button
                    key={slot.startTime}
                    onClick={() => handleSelectSlot(slot)}
                    className="px-3 py-2 border border-slate-300 text-sm font-medium text-slate-900 hover:border-primary-700 hover:text-primary-700 transition-colors text-center"
                  >
                    {formatTime(slot.startTime, timezone, locale)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* FORM */}
        {state === 'form' && (
          <div>
            <button
              onClick={() => setState('slot-selection')}
              className="text-sm text-blue-600 hover:underline mb-4 block"
            >
              {t.backToSlots}
            </button>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t.yourDetails}</h2>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Honeypot */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleFormChange}
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                tabIndex="-1"
                autoComplete="off"
              />
              {/* Name */}
              <div>
                <label htmlFor="bc-name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.name}
                </label>
                <input
                  id="bc-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder={t.namePlaceholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="bc-email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.email}
                </label>
                <input
                  id="bc-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder={t.emailPlaceholder}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.email ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-xs text-red-600" role="alert">
                    {formErrors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="bc-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.phone} *
                </label>
                <input
                  id="bc-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder={t.phonePlaceholder}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.phone ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                {formErrors.phone && (
                  <p className="mt-1 text-xs text-red-600" role="alert">
                    {formErrors.phone}
                  </p>
                )}
              </div>

              {/* Subject - hidden, sent internally via prefillSubject */}
              <input type="hidden" name="subject" value={formData.subject} />

              {/* Comments */}
              <div>
                <label htmlFor="bc-comments" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.comments}
                </label>
                <textarea
                  id="bc-comments"
                  name="comments"
                  rows={3}
                  value={formData.comments}
                  onChange={handleFormChange}
                  placeholder={t.commentsPlaceholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Privacy */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="bc-privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, privacy: e.target.checked }));
                    setFormErrors((prev) => ({ ...prev, privacy: undefined }));
                  }}
                  className={`mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                    formErrors.privacy ? 'border-red-400' : ''
                  }`}
                />
                <label htmlFor="bc-privacy" className="text-xs text-gray-600">
                  {t.privacyLabel}
                </label>
              </div>
              {formErrors.privacy && (
                <p className="text-xs text-red-600 -mt-2" role="alert">
                  {formErrors.privacy}
                </p>
              )}

              <button
                type="button"
                onClick={() => handleSubmit()}
                className="w-full py-3 bg-primary-700 text-white font-semibold hover:bg-primary-800 transition-colors text-sm"
              >
                {t.submit}
              </button>
            </form>
          </div>
        )}

        {/* SUBMITTING */}
        {state === 'submitting' && <Spinner label={t.submitting} />}

        {/* CONFIRMATION */}
        {state === 'confirmation' && booking && (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">{t.bookingConfirmed}</h2>
            <p className="text-sm text-gray-600 mb-4">{t.confirmationMessage}</p>

            <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t.date}</span>
                <span className="text-gray-800">{formatDate(booking.startTime, locale)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t.time}</span>
                <span className="text-gray-800">
                  {formatTime(booking.startTime, timezone, locale)} – {formatTime(booking.endTime, timezone, locale)}
                </span>
              </div>
              {timezone && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t.timezone}</span>
                  <span className="text-gray-800">{timezone}</span>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ERROR */}
        {state === 'error' && (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">{t.errorTitle}</h2>
            <p className="text-sm text-gray-600 mb-6">{errorMessage || t.errorGeneric}</p>
            <button
              onClick={handleReset}
              className="px-5 py-2 bg-primary-700 text-white text-sm font-semibold hover:bg-primary-800 transition-colors"
            >
              {t.backToStart}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingComponent;
